import { type Associations, type Driver, FuscaIdentificationTokenRequester, GraphQLDriversRequester, IdentificationTokenRequester, type TokenValidation } from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import {
  deleteDriverFailed,
  deleteDriverSucceeded,
  loadDriversFailed,
  loadDriversSucceeded,
  loadTokenAssociationFailed,
  loadTokenAssociationSucceeded,
  saveDriverFailed,
  saveDriverSucceeded,
  saveTokenAssociationFailed,
  saveTokenAssociationSucceeded,
  tokenValidationFailed,
  tokenValidationSucceeded,
} from '@cobliteam/store/src/Drivers/actionCreators';
import type { DeleteDriverRequest, SaveDriverRequest, SaveTokenAssociationRequest, TokenValidationRequest } from '@cobliteam/store/src/Drivers/actions';
import { DriverActionTypes } from '@cobliteam/store/src/Drivers/driverActionTypes';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'sonner';

const env = Environment.getInstance().getEnvironment();

export class DriversSagas {
  private identificationTokenRequester: IdentificationTokenRequester;

  private graphQLDriversRequester: GraphQLDriversRequester;

  private fuscaRequester = new FuscaIdentificationTokenRequester({
    host: env.apiHost,
  });

  constructor(graphQLDriversRequester: GraphQLDriversRequester, identificationTokenRequester: IdentificationTokenRequester) {
    this.identificationTokenRequester = identificationTokenRequester;
    this.graphQLDriversRequester = graphQLDriversRequester;
    this.requestLoadDriversSaga = this.requestLoadDriversSaga.bind(this);
    this.requestSaveTokenAssociation = this.requestSaveTokenAssociation.bind(this);
    this.requestTokenValidationSaga = this.requestTokenValidationSaga.bind(this);
    this.requestDeleteDriverSaga = this.requestDeleteDriverSaga.bind(this);
    this.requestLoadTokenAssociationsSaga = this.requestLoadTokenAssociationsSaga.bind(this);
    this.requestSaveDriver = this.requestSaveDriver.bind(this);
  }

  public *requestLoadDriversSaga() {
    try {
      const drivers: Driver[] = yield call(this.graphQLDriversRequester.getDrivers);
      yield put(loadDriversSucceeded(drivers));
    } catch (err) {
      yield put(loadDriversFailed());
      toast.error('Houve um erro ao carregar os motoristas, tente novamente mais tarde.');
    }
  }

  public *requestSaveTokenAssociation(action: SaveTokenAssociationRequest) {
    const { driverId, identifier } = action.payload;

    try {
      yield call(this.identificationTokenRequester.saveTokenAssociationV2, driverId, identifier);
      yield put(saveTokenAssociationSucceeded(driverId, identifier));
    } catch (err) {
      yield put(saveTokenAssociationFailed());
      toast.error('Houve um erro ao salvar a associação, tente novamente mais tarde.');
    }
  }

  public *requestTokenValidationSaga(action: TokenValidationRequest) {
    const identifier = action.payload;

    try {
      const tokenValidationData: TokenValidation = yield call(this.identificationTokenRequester.tokenValidationV2, identifier);
      yield put(tokenValidationSucceeded(tokenValidationData));
    } catch (err) {
      yield put(tokenValidationFailed());
      toast.error('Não foi possível validar o identificador. Por favor, entre em contato com o Atendimento.');
    }
  }

  public *requestDeleteDriverSaga(action: DeleteDriverRequest) {
    const { driverId } = action.payload;

    try {
      yield call(this.graphQLDriversRequester.deleteDriver, driverId);
      yield put(deleteDriverSucceeded(driverId));
      toast.success('Motorista excluído');
    } catch (err) {
      yield put(deleteDriverFailed());
      toast.error('Houve um erro ao excluir o motorista, tente novamente mais tarde.');
    }
  }

  public *requestLoadTokenAssociationsSaga() {
    try {
      const associations: Associations = yield call(this.fuscaRequester.getTokenAssociationsV2);
      yield put(loadTokenAssociationSucceeded(associations));
    } catch (err) {
      yield put(loadTokenAssociationFailed());
      toast.error('Houve um erro ao carregar as associações, tente novamente mais tarde.');
    }
  }

  public *requestSaveDriver(action: SaveDriverRequest) {
    const driver = action.payload;

    try {
      const saveDriverResponseData: Driver = yield call(this.graphQLDriversRequester.saveDriver, driver);
      yield put(saveDriverSucceeded(driver, saveDriverResponseData));
    } catch (err) {
      yield put(saveDriverFailed());
      toast.error('Houve um erro, tente novamente mais tarde.');
    }
  }
}

export function* DriversSagasListener() {
  const driversSaga = new DriversSagas(new GraphQLDriversRequester(), new IdentificationTokenRequester({ host: env.herbieApiHost }));
  yield all([
    takeLatest(DriverActionTypes.LOAD_DRIVERS_REQUEST, driversSaga.requestLoadDriversSaga),
    takeLatest(DriverActionTypes.DELETE_DRIVER_REQUEST, driversSaga.requestDeleteDriverSaga),
    takeLatest(DriverActionTypes.SAVE_TOKEN_ASSOCIATION_REQUEST, driversSaga.requestSaveTokenAssociation),
    takeLatest(DriverActionTypes.TOKEN_VALIDATION_REQUEST, driversSaga.requestTokenValidationSaga),
    takeLatest(DriverActionTypes.LOAD_TOKEN_ASSOCIATIONS_REQUEST, driversSaga.requestLoadTokenAssociationsSaga),
    takeLatest(DriverActionTypes.SAVE_DRIVER_REQUEST, driversSaga.requestSaveDriver),
  ]);
}
